import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { getAnalyticsApiBaseUrl } from '../../../config/api';
import { fetchWithAuth } from '../../../utils/auth';
import { PeriodConfig, formatNumber, tooltipStyle } from '../types';

const N: Record<string, string> = {
  IN: 'India', SA: 'Saudi Arabia', NG: 'Nigeria', PK: 'Pakistan', PH: 'Philippines',
  BR: 'Brazil', MY: 'Malaysia', ZA: 'South Africa', AE: 'UAE', UZ: 'Uzbekistan',
  LK: 'Sri Lanka', EG: 'Egypt', US: 'United States', OM: 'Oman', ID: 'Indonesia',
  SO: 'Somalia', RW: 'Rwanda', MN: 'Mongolia', UG: 'Uganda', CD: 'DR Congo',
  PG: 'Papua New Guinea', FJ: 'Fiji', AR: 'Argentina', SV: 'El Salvador', BE: 'Belgium',
  EC: 'Ecuador', AM: 'Armenia', KG: 'Kyrgyzstan', PE: 'Peru', ZW: 'Zimbabwe',
  ZM: 'Zambia', BO: 'Bolivia', GN: 'Guinea', JM: 'Jamaica', LR: 'Liberia',
  CO: 'Colombia', BT: 'Bhutan', KW: 'Kuwait', SY: 'Syria', QA: 'Qatar',
  TR: 'Turkey', MX: 'Mexico', VN: 'Vietnam', BH: 'Bahrain', IQ: 'Iraq',
  BD: 'Bangladesh', MA: 'Morocco', JO: 'Jordan', TZ: 'Tanzania', HN: 'Honduras',
  CI: 'Ivory Coast', KE: 'Kenya', XK: 'Kosovo', AL: 'Albania', PT: 'Portugal',
};

type SortKey = 'grossRevenue' | 'adSpend' | 'profit' | 'roi' | 'signups' | 'trialStarts' | 'renewals' | 'billingFailRate';

const CountryAnalysisTab: React.FC<{ period: PeriodConfig }> = ({ period }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('grossRevenue');
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedCountryName, setSelectedCountryName] = useState<string | null>(null);
  const [countryDeep, setCountryDeep] = useState<any>(null);
  const [deepLoading, setDeepLoading] = useState(false);
  const [spendTree, setSpendTree] = useState<any>(null);
  const [spendTreeLoading, setSpendTreeLoading] = useState(false);
  const [showAllCountries, setShowAllCountries] = useState(false);

  const getDateRange = useCallback((): { from: string; to: string } => {
    if (period.type === 'custom') return { from: period.from, to: period.to };
    const days = period.type === 'period' ? parseInt(period.days) : 3;
    const to = new Date();
    to.setUTCDate(to.getUTCDate() - 1);
    const from = new Date(to);
    from.setUTCDate(from.getUTCDate() - days + 1);
    return {
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10),
    };
  }, [period]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const base = getAnalyticsApiBaseUrl();
      const { from, to } = getDateRange();
      const res = await fetchWithAuth(`${base}/analytics/country-summary?from=${from}&to=${to}`);
      if (res.ok) {
        const result = await res.json();
        setData(result);
      }
    } catch (err) {
      console.error('Country analysis fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadCountryDeep = useCallback(async (cc: string, countryName?: string) => {
    setDeepLoading(true);
    setSpendTreeLoading(true);
    setSelectedCountry(cc);
    setSelectedCountryName(countryName || N[cc] || cc);
    setSpendTree(null);
    const base = getAnalyticsApiBaseUrl();
    const { from, to } = getDateRange();

    // Fire both fetches in parallel — the spend tree is a separate live
    // call to FB / Google / TikTok APIs (no DB cache) so it's slower than
    // the country-deep query. Render whichever arrives first.
    fetchWithAuth(`${base}/analytics/country-deep?country=${cc}&from=${from}&to=${to}`)
      .then((res) => res.ok ? res.json() : null)
      .then((d) => setCountryDeep(d))
      .catch((err) => console.error('Country deep fetch error:', err))
      .finally(() => setDeepLoading(false));

    fetchWithAuth(`${base}/analytics/country-spend-breakdown?country=${cc}&from=${from}&to=${to}`)
      .then((res) => res.ok ? res.json() : null)
      .then((d) => setSpendTree(d))
      .catch((err) => console.error('Country spend tree fetch error:', err))
      .finally(() => setSpendTreeLoading(false));
  }, [getDateRange]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  if (loading) return <LoadingSpinner />;

  const sorted = [...data].map(d => ({
    ...d,
    profit: (d.netRevenue || 0) - (d.adSpend || 0),
    roi: (d.adSpend || 0) > 0 ? (((d.netRevenue || 0) - (d.adSpend || 0)) / (d.adSpend || 1)) * 100 : null,
  })).sort((a, b) => {
    const av = a[sortKey] || 0;
    const bv = b[sortKey] || 0;
    return sortAsc ? av - bv : bv - av;
  });

  const totalSpend = data.reduce((s, d) => s + (d.adSpend || 0), 0);
  const totalRev = data.reduce((s, d) => s + (d.grossRevenue || 0), 0);
  const totalNet = data.reduce((s, d) => s + (d.netRevenue || 0), 0);
  const totalProfit = totalNet - totalSpend;

  const { from, to } = getDateRange();

  const SortHeader = ({ label, k, w }: { label: string; k: SortKey; w: string }) => (
    <th
      className={`pb-3 font-medium cursor-pointer hover:text-[#FF9C15] ${w} ${sortKey === k ? 'text-[#FF9C15]' : ''}`}
      onClick={() => handleSort(k)}
    >
      {label} {sortKey === k ? (sortAsc ? '↑' : '↓') : ''}
    </th>
  );

  const getVerdict = (d: any): { text: string; color: string } => {
    const spend = d.adSpend || 0;
    const net = d.netRevenue || 0;
    const gross = d.grossRevenue || 0;
    const profit = net - spend;  // Net profit = what you keep minus what you spent
    if (spend === 0 && gross > 5) return { text: 'FREE REV', color: 'text-cyan-400' };
    if (spend === 0 && gross > 0) return { text: 'ORGANIC', color: 'text-gray-400' };
    if (profit > 20) return { text: 'SCALE UP', color: 'text-emerald-400' };
    if (profit > 5) return { text: 'KEEP', color: 'text-green-400' };
    if (profit > -2) return { text: 'MONITOR', color: 'text-yellow-400' };
    if (gross === 0 && spend > 2) return { text: 'STOP', color: 'text-red-500' };
    if (profit > -10) return { text: 'WATCH', color: 'text-orange-400' };
    return { text: 'REDUCE', color: 'text-red-400' };
  };

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-gray-400 uppercase">Period</p>
          <p className="text-lg font-bold text-white mt-1">{from} → {to}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-gray-400 uppercase">Ad Spend</p>
          <p className="text-2xl font-bold text-white mt-1">${formatNumber(totalSpend)}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-gray-400 uppercase">Gross Revenue</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">${formatNumber(totalRev)}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-gray-400 uppercase">Net Revenue</p>
          <p className="text-2xl font-bold text-green-400 mt-1">${formatNumber(totalNet)}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-gray-400 uppercase">Net Profit</p>
          <p className={`text-2xl font-bold mt-1 ${totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            ${formatNumber(totalProfit)}
          </p>
        </div>
      </div>

      {/* Country table */}
      <ChartCard title={`Country Performance (${data.length} countries${!showAllCountries ? ' — showing top 20' : ''})`}>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0a0b10] z-10">
              <tr className="text-left text-gray-400 border-b border-white/10">
                <th className="pb-3 font-medium w-40">Country</th>
                <SortHeader label="Spend" k="adSpend" w="w-20 text-right" />
                <SortHeader label="Revenue" k="grossRevenue" w="w-20 text-right" />
                <SortHeader label="Profit" k="profit" w="w-20 text-right" />
                <SortHeader label="ROI" k="roi" w="w-16 text-right" />
                <SortHeader label="Signups" k="signups" w="w-16 text-right" />
                <SortHeader label="Trials" k="trialStarts" w="w-16 text-right" />
                <SortHeader label="Renew" k="renewals" w="w-14 text-right" />
                <th className="pb-3 font-medium w-14 text-right">BillFl</th>
                <SortHeader label="BF%" k="billingFailRate" w="w-14 text-right" />
                <th className="pb-3 font-medium w-14 text-right">T→S%</th>
                <th className="pb-3 font-medium w-14 text-right">Conv%</th>
                <th className="pb-3 font-medium w-20 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {(showAllCountries ? sorted : sorted.slice(0, 20)).map((d: any) => {
                const cc = d.countryCode || d._id;
                const countryName = d.countryName || N[cc] || cc;
                const spend = d.adSpend || 0;
                const gross = d.grossRevenue || 0;
                const net = d.netRevenue || 0;
                const profit = net - spend;
                const roi = spend > 0 ? ((net - spend) / spend * 100) : null;
                const verdict = getVerdict(d);
                return (
                  <tr
                    key={cc}
                    className={`border-b border-white/5 hover:bg-white/5 cursor-pointer ${selectedCountry === cc ? 'bg-white/10' : ''}`}
                    onClick={() => loadCountryDeep(cc, countryName)}
                  >
                    <td className="py-2.5 font-medium text-white">{countryName}</td>
                    <td className="py-2.5 text-right text-gray-300">{spend > 0 ? `$${spend.toFixed(0)}` : '-'}</td>
                    <td className="py-2.5 text-right text-green-400 font-medium">${gross.toFixed(0)}</td>
                    <td className={`py-2.5 text-right font-medium ${profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      ${profit.toFixed(0)}
                    </td>
                    <td className="py-2.5 text-right text-gray-300">
                      {roi != null ? `${roi.toFixed(0)}%` : spend === 0 ? 'FREE' : '-'}
                    </td>
                    <td className="py-2.5 text-right text-gray-300">{formatNumber(d.signups || 0)}</td>
                    <td className="py-2.5 text-right text-gray-300">{d.trialStarts || 0}</td>
                    <td className="py-2.5 text-right text-gray-300">{d.renewals || 0}</td>
                    <td className="py-2.5 text-right text-gray-300">{d.billingIssues || 0}</td>
                    <td className="py-2.5 text-right text-gray-300">{(d.billingFailRate || 0).toFixed(0)}%</td>
                    <td className="py-2.5 text-right text-gray-300">{(d.signupToTrial || 0).toFixed(1)}%</td>
                    <td className="py-2.5 text-right text-gray-300">{(d.trialConversion || 0).toFixed(1)}%</td>
                    <td className={`py-2.5 text-center text-xs font-semibold ${verdict.color}`}>{verdict.text}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {sorted.length > 20 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => setShowAllCountries(!showAllCountries)}
              className="px-6 py-2 text-sm font-semibold rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10 transition-all"
            >
              {showAllCountries ? `Show Top 20 ↑` : `Show All ${sorted.length} Countries ↓`}
            </button>
          </div>
        )}
      </ChartCard>

      {/* Country deep dive */}
      {selectedCountry && (
        <div className="space-y-4 mt-6 border-t border-white/10 pt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-white">
              {selectedCountryName || N[selectedCountry] || selectedCountry} — Deep Analysis
            </h3>
            <button onClick={() => { setSelectedCountry(null); setSelectedCountryName(null); setCountryDeep(null); }} className="text-xs text-gray-500 hover:text-white px-3 py-1 border border-white/10 rounded-lg">
              Close
            </button>
          </div>

          {deepLoading ? <LoadingSpinner /> : countryDeep?.dailyData && (() => {
            const days = countryDeep.dailyData;
            const t = countryDeep.totals || {};
            const profit = (t.netRevenue || 0) - (t.adSpend || 0);
            const roi = t.adSpend > 0 ? (((t.netRevenue || 0) - t.adSpend) / t.adSpend * 100) : null;

            // Aggregate products across all days
            const productMap: Record<string, { revenue: number; count: number }> = {};
            days.forEach((d: any) => {
              (d.revenue?.byProduct || []).forEach((p: any) => {
                if (!productMap[p.product]) productMap[p.product] = { revenue: 0, count: 0 };
                productMap[p.product].revenue += p.revenue;
                productMap[p.product].count += p.count;
              });
            });
            const products = Object.entries(productMap)
              .map(([product, v]) => ({ product, ...v }))
              .sort((a, b) => b.revenue - a.revenue);

            // Cancel reasons aggregate
            const cancelMap: Record<string, number> = {};
            days.forEach((d: any) => {
              const cr = d.subscriptions?.cancelReasons || {};
              Object.entries(cr).forEach(([k, v]) => { cancelMap[k] = (cancelMap[k] || 0) + (v as number); });
            });

            return (
              <>
                {/* KPI cards */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  {[
                    { label: 'Ad Spend', value: `$${(t.adSpend || 0).toFixed(2)}`, color: 'text-white' },
                    { label: 'Gross Revenue', value: `$${(t.grossRevenue || 0).toFixed(2)}`, color: 'text-emerald-400' },
                    { label: 'Net Revenue', value: `$${(t.netRevenue || 0).toFixed(2)}`, color: 'text-green-400' },
                    { label: 'Net Profit', value: `$${profit.toFixed(2)}`, color: profit >= 0 ? 'text-emerald-400' : 'text-red-400' },
                    { label: 'Net ROI', value: roi != null ? `${roi.toFixed(0)}%` : 'FREE', color: (roi || 0) > 0 ? 'text-emerald-400' : 'text-red-400' },
                  ].map((s) => (
                    <div key={s.label} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                      <p className="text-xs text-gray-500 uppercase">{s.label}</p>
                      <p className={`text-xl font-bold ${s.color} mt-1`}>{s.value}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    { label: 'Signups', value: t.signups || 0 },
                    { label: 'Trial Starts', value: t.trialStarts || 0 },
                    { label: 'Renewals', value: t.renewals || 0 },
                    { label: 'Cancellations', value: t.cancellations || 0 },
                    { label: 'Billing Issues', value: t.billingIssues || 0 },
                    { label: 'Ad Installs', value: t.adInstalls || 0 },
                  ].map((s) => (
                    <div key={s.label} className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-500">{s.label}</p>
                      <p className="text-lg font-bold text-white">{formatNumber(s.value)}</p>
                    </div>
                  ))}
                </div>

                {/* Where the money goes — nested spend tree */}
                <ChartCard title={`Where the money goes${spendTree ? ` · $${spendTree.totalSpend?.toFixed(2)}` : ''}`}>
                  {spendTreeLoading && !spendTree ? (
                    <p className="text-sm text-gray-500 py-4">Loading spend breakdown from FB / Google / TikTok…</p>
                  ) : !spendTree || (spendTree.networks || []).length === 0 ? (
                    <p className="text-sm text-gray-500 py-4">No paid spend recorded for this country in the selected window.</p>
                  ) : (
                    <div className="space-y-3">
                      {spendTree.networks.map((n: any) => {
                        const networkColor = n.channel === 'facebook' ? 'text-blue-300 bg-blue-500/10 border-blue-500/30'
                          : n.channel === 'google_ads' ? 'text-green-300 bg-green-500/10 border-green-500/30'
                          : 'text-pink-300 bg-pink-500/10 border-pink-500/30';
                        const networkLabel = n.channel === 'google_ads' ? 'Google' : n.channel === 'facebook' ? 'Facebook' : 'TikTok';
                        return (
                          <details key={n.channel} className="rounded-xl border border-white/10 bg-white/5" open>
                            <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold flex items-center justify-between hover:bg-white/5 rounded-xl">
                              <span className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-bold border ${networkColor}`}>{networkLabel}</span>
                                <span className="text-gray-400 text-xs">{(n.accounts || []).length} account{(n.accounts || []).length === 1 ? '' : 's'}</span>
                              </span>
                              <span className="text-emerald-400 font-bold">${n.spend.toFixed(2)}</span>
                            </summary>
                            <div className="px-4 pb-3 space-y-2">
                              {(n.accounts || []).map((acct: any) => (
                                <details key={acct.accountId} className="ml-2 rounded-lg border border-white/10 bg-white/5" open>
                                  <summary className="cursor-pointer px-3 py-2 text-xs font-medium flex items-center justify-between hover:bg-white/5 rounded-lg">
                                    <span className="flex items-center gap-2">
                                      <span className="text-gray-300">{acct.accountName}</span>
                                      <span className="text-gray-500">·</span>
                                      <span className="text-gray-500">{(acct.campaigns || []).length} campaign{(acct.campaigns || []).length === 1 ? '' : 's'}</span>
                                    </span>
                                    <span className="text-emerald-400 font-semibold">${acct.spend.toFixed(2)}</span>
                                  </summary>
                                  <div className="px-3 pb-2 space-y-1.5">
                                    {(acct.campaigns || []).map((c: any) => (
                                      <details key={c.campaignId} className="ml-2 rounded border border-white/5 bg-black/20">
                                        <summary className="cursor-pointer px-3 py-1.5 text-xs flex items-center justify-between hover:bg-white/5 rounded">
                                          <span className="flex items-center gap-2">
                                            <span className="text-white truncate max-w-[280px]" title={c.campaignName}>{c.campaignName}</span>
                                            <span className="text-gray-500 hidden lg:inline">·</span>
                                            <span className="text-gray-500 hidden lg:inline">
                                              {c.installs > 0 && `${formatNumber(c.installs)} inst`}
                                              {c.installs > 0 && c.paid > 0 && ' · '}
                                              {c.paid > 0 && `${c.paid} paid`}
                                            </span>
                                          </span>
                                          <span className="text-emerald-400 font-medium">${c.spend.toFixed(2)}</span>
                                        </summary>
                                        <div className="px-3 pb-2">
                                          <table className="w-full text-xs">
                                            <thead>
                                              <tr className="text-gray-500 border-b border-white/5">
                                                <th className="py-1 text-left font-normal">Ad set</th>
                                                <th className="py-1 text-right font-normal">Spend</th>
                                                <th className="py-1 text-right font-normal">Impr</th>
                                                <th className="py-1 text-right font-normal">Clicks</th>
                                                <th className="py-1 text-right font-normal">Inst</th>
                                                <th className="py-1 text-right font-normal">Paid</th>
                                                <th className="py-1 text-right font-normal">CPI</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {(c.adsets || []).map((a: any, i: number) => {
                                                const cpi = a.installs > 0 ? a.spend / a.installs : 0;
                                                return (
                                                  <tr key={`${a.adsetId}-${i}`} className="border-b border-white/5 last:border-0">
                                                    <td className="py-1 text-gray-300 truncate max-w-[260px]" title={a.adsetName}>{a.adsetName}</td>
                                                    <td className="py-1 text-right text-emerald-400">${a.spend.toFixed(2)}</td>
                                                    <td className="py-1 text-right text-gray-400">{formatNumber(a.impressions)}</td>
                                                    <td className="py-1 text-right text-gray-400">{formatNumber(a.clicks)}</td>
                                                    <td className="py-1 text-right text-gray-400">{formatNumber(a.installs)}</td>
                                                    <td className="py-1 text-right text-gray-300">{a.paid || '—'}</td>
                                                    <td className="py-1 text-right text-gray-400">{cpi > 0 ? `$${cpi.toFixed(3)}` : '—'}</td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      </details>
                                    ))}
                                  </div>
                                </details>
                              ))}
                            </div>
                          </details>
                        );
                      })}
                    </div>
                  )}
                </ChartCard>

                {/* Charts row 1: Revenue + Ad Spend */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <ChartCard title="Daily Revenue vs Ad Spend">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={days.map((d: any) => ({
                        date: d.date?.slice(5),
                        revenue: d.revenue?.gross || 0,
                        spend: d.adSpend?.spend || 0,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                        <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v: number) => `$${v}`} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Bar dataKey="revenue" fill="#34d399" radius={[2, 2, 0, 0]} name="Revenue" />
                        <Bar dataKey="spend" fill="#ef4444" radius={[2, 2, 0, 0]} name="Ad Spend" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  <ChartCard title="Daily Signups & Trial Starts">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={days.map((d: any) => ({
                        date: d.date?.slice(5),
                        signups: d.signups?.total || 0,
                        trials: d.subscriptions?.trialStarts || 0,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                        <YAxis stroke="#64748b" fontSize={11} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Bar dataKey="signups" fill="#38bdf8" radius={[2, 2, 0, 0]} name="Signups" />
                        <Bar dataKey="trials" fill="#FF9C15" radius={[2, 2, 0, 0]} name="Trials" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>

                {/* Charts row 2: Subscriptions + Installs */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <ChartCard title="Subscription Events">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={days.map((d: any) => ({
                        date: d.date?.slice(5),
                        renewals: d.subscriptions?.renewals || 0,
                        cancels: d.subscriptions?.cancellations || 0,
                        billing: d.subscriptions?.billingIssues || 0,
                        trials: d.subscriptions?.trialStarts || 0,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                        <YAxis stroke="#64748b" fontSize={11} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Bar dataKey="trials" fill="#38bdf8" radius={[2, 2, 0, 0]} name="Trials" />
                        <Bar dataKey="renewals" fill="#34d399" radius={[2, 2, 0, 0]} name="Renewals" />
                        <Bar dataKey="cancels" fill="#f97316" radius={[2, 2, 0, 0]} name="Cancellations" />
                        <Bar dataKey="billing" fill="#ef4444" radius={[2, 2, 0, 0]} name="Billing Issues" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  <ChartCard title="Ad Installs vs CPI">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={days.map((d: any) => ({
                        date: d.date?.slice(5),
                        installs: d.adSpend?.installs || 0,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                        <YAxis stroke="#64748b" fontSize={11} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Bar dataKey="installs" fill="#c084fc" radius={[4, 4, 0, 0]} name="Installs" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>

                {/* Product breakdown + Cancel reasons */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <ChartCard title={`Revenue by Product (${days.length} days total)`}>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {products.map((p, i) => (
                        <div key={i} className="flex justify-between text-sm py-1 border-b border-white/5">
                          <span className="text-gray-400">{p.product}</span>
                          <div className="flex gap-4">
                            <span className="text-gray-500">{p.count} txns</span>
                            <span className="text-green-400 font-semibold">${p.revenue.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ChartCard>

                  <ChartCard title="Cancellation Reasons">
                    <div className="space-y-3">
                      {Object.entries(cancelMap).sort(([,a],[,b]) => (b as number) - (a as number)).map(([reason, count]) => {
                        const total = Object.values(cancelMap).reduce((s, v) => s + (v as number), 0);
                        const pct = total > 0 ? ((count as number) / total * 100) : 0;
                        return (
                          <div key={reason}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-400">{reason}</span>
                              <span className="text-gray-300">{count as number} ({pct.toFixed(0)}%)</span>
                            </div>
                            <div className="w-full bg-white/5 rounded-full h-2">
                              <div className={`h-2 rounded-full ${reason === 'BILLING_ERROR' ? 'bg-red-500' : reason === 'UNSUBSCRIBE' ? 'bg-orange-400' : 'bg-gray-500'}`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ChartCard>
                </div>

                {/* Coin breakdown */}
                <ChartCard title="Coin Pack Sales">
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    {['smallPack', 'mediumPack', 'largePack', 'megaPack', 'premiumPack'].map((pack) => {
                      const labels: Record<string,string> = { smallPack: 'Small ($0.29)', mediumPack: 'Medium ($0.79)', largePack: 'Large ($2.49)', megaPack: 'Mega ($9.49)', premiumPack: 'Premium ($44.99)' };
                      const totals = days.reduce((acc: any, d: any) => ({
                        purchases: acc.purchases + (d.coins?.[pack]?.purchases || 0),
                        revenue: acc.revenue + (d.coins?.[pack]?.revenue || 0),
                      }), { purchases: 0, revenue: 0 });
                      return (
                        <div key={pack} className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                          <p className="text-xs text-gray-500">{labels[pack]}</p>
                          <p className="text-sm font-bold text-white mt-1">{totals.purchases} sales</p>
                          <p className="text-xs text-green-400">${totals.revenue.toFixed(2)}</p>
                        </div>
                      );
                    })}
                  </div>
                </ChartCard>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default CountryAnalysisTab;
